// sketchSpline — плавная кривая через заданные точки.
//
// Нужна для обводов, которые нельзя собрать из отрезков и дуг: корпус с
// плавным переходом, профиль лопасти, эргономичная ручка. В Fusion это
// «Сплайн»: кликаешь опорные точки, между ними ложится гладкая кривая.
//
// Считаем сплайн Катмулла–Рома: он проходит ЧЕРЕЗ опорные точки, а не мимо
// них. Для черчения это важно — человек ставит точку там, где хочет видеть
// кривую, а не там, где она изогнётся «примерно».

/**
 * Разбить сплайн на ломаную.
 *
 * pts      — опорные точки [{x, y}, ...];
 * perSpan  — сколько отрезков на каждый пролёт между опорными точками;
 * closed   — замкнуть кривую.
 *
 * Возвращает [{x, y}, ...] — точки ломаной, пригодные и для отрисовки,
 * и для передачи в ядро как контур.
 */
export function splineToPolyline(pts, perSpan = 12, closed = false) {
    const n = pts.length;
    if (n < 2) return pts.slice();
    // Две точки — это просто отрезок, гнуть нечего.
    if (n === 2 && !closed) return pts.slice();

    /*
     * Для крайних пролётов нужны «соседи за краем». У незамкнутой кривой их
     * нет — отражаем ближайшую точку, тогда концы выходят прямыми, без
     * паразитного загиба наружу.
     */
    const at = (i) => {
        if (closed) return pts[((i % n) + n) % n];
        if (i < 0) return { x: 2 * pts[0].x - pts[1].x, y: 2 * pts[0].y - pts[1].y };
        if (i > n - 1) {
            return {
                x: 2 * pts[n - 1].x - pts[n - 2].x,
                y: 2 * pts[n - 1].y - pts[n - 2].y,
            };
        }
        return pts[i];
    };

    const out = [];
    const spans = closed ? n : n - 1;
    for (let i = 0; i < spans; i++) {
        const p0 = at(i - 1);
        const p1 = at(i);
        const p2 = at(i + 1);
        const p3 = at(i + 2);
        for (let s = 0; s < perSpan; s++) {
            const t = s / perSpan;
            const t2 = t * t;
            const t3 = t2 * t;
            // Катмулл–Ром с натяжением 0.5 — стандартная форма.
            out.push({
                x: 0.5 * ((2 * p1.x)
                    + (-p0.x + p2.x) * t
                    + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2
                    + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
                y: 0.5 * ((2 * p1.y)
                    + (-p0.y + p2.y) * t
                    + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2
                    + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
            });
        }
    }
    // Последнюю точку добавляем явно: цикл её не доходит (t < 1).
    if (!closed) out.push({ x: pts[n - 1].x, y: pts[n - 1].y });
    return out;
}
